"use strict";

import React from "react";
import { render } from "react-dom";
import { Provider, connect } from "react-redux";
import { combineReducers, createStore } from "redux";
import {
  createSelector,
  createStructuredSelector as combineSelectors,
} from "reselect";
import {
  DropdownButton,
  ButtonGroup,
  Button,
  MenuItem,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import Autocomplete from "react-autocomplete";
import {
  GlyphCollapseToggle,
  GlyphExistingNote,
  GlyphSharedMeasure,
  GlyphFilterPerformanceHighest,
  GlyphFilterPerformanceLowest,
  GlyphFilterTrendIncrease,
  GlyphFilterTrendDecrease,
  GlyphRangeOneYear,
  GlyphRangeTwoYears,
  GlyphRangeAllYears,
} from "./icons.js";
import { Nav, links, user } from "./nav.js";
import { IndividualGraphicRep } from "./rep_graphics.js";
import { PuzzleLegend } from "./puzzle.js";
import { PopupGraph } from "./graph_full.js";
import { ScrollHelper } from "./scroll_helper.js";
import {
  RawScore,
  ComboScore,
  ChangeDisplay,
  CloseButton,
  AsyncAccordions,
  LoginModal,
  NoteShareModal,
  AbstractTrackURL,
} from "./components.js";
import {
  scrollIntoViewIfNeeded,
  simpleTitleCase,
  plural,
  verboseTimestamp,
  topSorted,
  countTruthyValues,
} from "./shared.js";
import { PRODUCT_KEYS as S } from "./data_helpers.js";
import data from "./data.js";

const L = data.survey.labels;

const SET_ACTIVE_VIEW = "SET_ACTIVE_VIEW";
const SET_LEVEL_AND_SLUG = "SET_LEVEL_AND_SLUG";
const SELECT_LEVEL_AND_SLUG = "SELECT_LEVEL_AND_SLUG";
const SET_ACTIVE_FILTER = "SET_ACTIVE_FILTER";
const SET_GROUP_VIEW = "SET_GROUP_VIEW";
const SET_EXPANDED_RESULTS = "SET_EXPANDED_RESULTS"; // group view table
const SET_EXPANDED_ITEMS = "SET_EXPANDED_ITEMS"; // measure items
const SET_ACTIVE_COMPARISON = "SET_ACTIVE_COMPARISON";
const SORT_COLUMN = "SORT_COLUMN";

const ADD_PENDING_FILTER = "ADD_PENDING_FILTER";
const REMOVE_PENDING_FILTER = "REMOVE_PENDING_FILTER";
const CANCEL_CUSTOM_FILTER_EDITING = "CANCEL_CUSTOM_FILTER_EDITING";
const FINISH_CUSTOM_FILTER_EDITING = "FINISH_CUSTOM_FILTER_EDITING";
const SET_FILTER_QUERY = "SET_FILTER_QUERY";

const FILTER_HIGHEST = "highest";
const FILTER_LOWEST = "lowest";
const FILTER_INCREASE = "increased_most";
const FILTER_DECREASE = "decreased_most";

const FILTER_MAPPINGS = {
  [FILTER_HIGHEST]: "-score",
  [FILTER_LOWEST]: "+score",
  [FILTER_INCREASE]: "-change",
  [FILTER_DECREASE]: "+change",
};

function activeLevel(state = "essentials", action) {
  switch (action.type) {
    case SET_LEVEL_AND_SLUG:
      return action.payload[0];
    default:
      return state;
  }
}

function activeSlug(state = "overall", action) {
  switch (action.type) {
    case SET_LEVEL_AND_SLUG:
      return action.payload[1];
    default:
      return state;
  }
}

function activeView(state = "performance", action) {
  switch (action.type) {
    case SET_LEVEL_AND_SLUG:
      let [level, slug] = action.payload;
      return level === "measures" && slug === "all" ? "performance" : state;
    case SET_ACTIVE_VIEW:
      return action.payload;
    case SET_GROUP_VIEW:
      if (action.payload === "by_school" && state === "trends") return "change";
    default:
      return state;
  }
}

function activeComparisonSlug(state = null, action) {
  switch (action.type) {
    case SET_ACTIVE_COMPARISON:
      return action.payload;
    case SET_LEVEL_AND_SLUG:
      return null;
    default:
      return state;
  }
}

function activeFilter(state = null, action) {
  switch (action.type) {
    case SET_ACTIVE_FILTER:
      return action.payload && action.payload._editCustom
        ? state
        : action.payload;
    case FINISH_CUSTOM_FILTER_EDITING:
      let selectedMeasures = action.payload;
      return selectedMeasures.length ? { custom: true } : null;
    case SET_LEVEL_AND_SLUG:
      // clear filters when "navigating"
      return null;
    default:
      return state;
  }
}

function customFilterMeasures(state = null, action) {
  switch (action.type) {
    case FINISH_CUSTOM_FILTER_EDITING:
      let selectedMeasures = action.payload;
      return selectedMeasures.length ? selectedMeasures : null;
    default:
      return state;
  }
}

function filterEditorQuery(state = "", action) {
  // WORKAROUND: ideally we wouldn't have to track this, see https://github.com/reactjs/react-autocomplete/issues/189
  switch (action.type) {
    case SET_FILTER_QUERY:
      return action.payload;
    case SET_ACTIVE_FILTER:
    case ADD_PENDING_FILTER:
      // reset when appropriate
      return "";
    default:
      return state;
  }
}

function pendingFilterMeasures(state = null, action) {
  switch (action.type) {
    case SET_ACTIVE_FILTER:
      // ~HACK: we initiate editing via this action to keep Filter dispatch logic simpler
      return action.payload && action.payload._editCustom
        ? action.payload.pendingFilterMeasures
        : state;
    case ADD_PENDING_FILTER:
      return [...state, action.payload];
    case REMOVE_PENDING_FILTER:
      return state.filter((s) => s !== action.payload);
    case CANCEL_CUSTOM_FILTER_EDITING:
    case FINISH_CUSTOM_FILTER_EDITING:
      return null;
    default:
      return state;
  }
}

function selectedLevel(state = null, action) {
  switch (action.type) {
    case SELECT_LEVEL_AND_SLUG:
    case SET_LEVEL_AND_SLUG: // NOTE: when activating something, we also select it.
      return action.payload[0];
    case SET_ACTIVE_FILTER:
    case SET_ACTIVE_COMPARISON:
      // (new filter/comparison might hide selectedObject)
      return null;
    default:
      return state;
  }
}

function selectedSlug(state = null, action) {
  switch (action.type) {
    case SELECT_LEVEL_AND_SLUG:
    case SET_LEVEL_AND_SLUG:
      return action.payload[1];
    case SET_ACTIVE_FILTER:
    case SET_ACTIVE_COMPARISON:
      return null;
    default:
      return state;
  }
}

function groupView(state = "aggregate", action) {
  switch (action.type) {
    case SET_GROUP_VIEW:
      return data.target.is_group ? action.payload : state;
    case SET_LEVEL_AND_SLUG:
      let [level, slug] = action.payload;
      return level === "measures" && slug === "all" ? "aggregate" : state;
    default:
      return state;
  }
}

function expandedResults(state = false, action) {
  switch (action.type) {
    case SET_EXPANDED_RESULTS:
      return action.payload;
    case SET_GROUP_VIEW:
    case SET_LEVEL_AND_SLUG:
      return false;
    default:
      return state;
  }
}

function expandedItems(state = [], action) {
  switch (action.type) {
    case SET_EXPANDED_ITEMS:
      return action.payload;
    case SET_LEVEL_AND_SLUG:
      return [];
    default:
      return state;
  }
}

function sortColumn(state = "-score", action) {
  switch (action.type) {
    case SORT_COLUMN:
      return action.payload;
    case SET_LEVEL_AND_SLUG:
      // HACK: this is ill-adivised in so many ways…. Reducers should be pure functions not accessing the store,
      //       overwriting the `action` parameter is a maintenance liability, and we're topping it off with a case fall-through!
      //       Alternatives would be to create a special reducer just for this cross-value case, or modify every dispatch of
      //       SET_LEVEL_AND_SLUG to also dispatch a SET_ACTIVE_VIEW. Ulimately, this hack seems cleaner/simpler overall.
      let payload = store.getState().activeView;
      action = { payload }; // NOTE: definitely don't modify the original `action` object here!
    case SET_ACTIVE_VIEW:
      switch (action.payload) {
        case "performance":
          return "-score";
        case "change":
        case "trends":
          return "-change";
        default:
          return state;
      }
    case SET_ACTIVE_FILTER:
      return action.payload && action.payload.sorted
        ? FILTER_MAPPINGS[action.payload.sorted]
        : state;
    default:
      return state;
  }
}

const NOTE_EDIT = "NOTE_EDIT";
const NOTE_TEXT = "NOTE_TEXT";
const NOTE_SAVE = "NOTE_SAVE";
const NOTE_FAIL = "NOTE_FAIL";
const NOTE_DONE = "NOTE_DONE";
const NOTE_EXIT = "NOTE_EXIT";
const ANALYSIS_DIALOG = "ANALYSIS_DIALOG";
const ANALYSIS_UPDATE = "ANALYSIS_UPDATE";
const ANALYSIS_SAVED = "ANALYSIS_SAVED";
const LOGIN_NEEDED = "LOGIN_NEEDED";

window.addEventListener(
  "beforeunload",
  function (evt) {
    let { notes } = store.getState();
    let hasPendingNote = Object.keys(notes).some((k) => notes[k]._modified);
    if (hasPendingNote) {
      // NOTE: seems that browsers no longer use this message, providing only their own.
      return (evt.returnValue =
        "You are currently editing one or more measure notes.");
    }
  },
  false,
);

let initialNotes = {};
Object.keys(data.notesByContextByMeasure).forEach((measure) => {
  let notesByContext = data.notesByContextByMeasure[measure];
  Object.keys(notesByContext).forEach((context) => {
    let key = `${measure}/${context}`;
    initialNotes[key] = notesByContext[context];
  });
});

function notes(state = initialNotes, action) {
  switch (action.type) {
    case NOTE_EDIT: {
      let [measure, context] = action.payload;
      let key = `${measure}/${context}`;
      let note = state[key] || { measure, context, observation: "" };
      if (note._editing) return state; // (particularly to avoid overwriting `NOTE_SAVE` state)
      return {
        ...state,
        [key]: { ...note, _original: note.observation, _editing: true },
      };
    }
    case NOTE_TEXT: {
      let [measure, context, observation] = action.payload;
      let key = `${measure}/${context}`;
      let _modified = observation !== state[key]._original;
      return { ...state, [key]: { ...state[key], observation, _modified } };
    }
    case NOTE_SAVE: {
      let [measure, context] = action.payload;
      let key = `${measure}/${context}`;
      return { ...state, [key]: { ...state[key], _editing: "save" } };
    }
    case NOTE_FAIL: {
      let [measure, context] = action.payload;
      let key = `${measure}/${context}`;
      return { ...state, [key]: { ...state[key], _editing: "error" } };
    }
    case NOTE_EXIT:
    case NOTE_DONE: {
      let [measure, context, fields] = action.payload;
      let key = `${measure}/${context}`;
      let newState = { ...state, [key]: { ...state[key] } };
      if (!newState[key].observation) delete newState[key];
      else {
        delete newState[key]._modified;
        if (action.type === NOTE_EXIT) {
          newState[key].observation = newState[key]._original;
          delete newState[key]._original;
          delete newState[key]._editing;
        } else {
          newState[key]._original = newState[key].observation;
          newState[key]._editing = true;
        }
        if (fields) {
          Object.assign(newState[key], fields);
        }
      }
      return newState;
    }
    default:
      return state;
  }
}

function analyses(state = data.analysesByMeasure, action) {
  // TODO: this (and notes too…) should get migrated to user_data reducers!!
  // [this is ± copy/pasta from there…]
  let objectsOfType = state;
  switch (action.type) {
    case ANALYSIS_UPDATE: {
      let [objKey, newFields] = action.payload;
      let object = objectsOfType[objKey];
      let _original = object._original || object;
      let _modified = Object.keys(_original).some(
        (k) => (k in newFields ? newFields[k] : object[k]) !== _original[k],
      );
      return {
        ...objectsOfType,
        [objKey]: { ...object, _original, _modified, ...newFields },
      };
    }
    case ANALYSIS_SAVED: {
      let [objKey] = action.payload;
      let {
        _original: _o,
        _placeholder: _p,
        ...object
      } = objectsOfType[objKey];
      return { ...objectsOfType, [objKey]: { ...object, _modified: false } };
    }
    default:
      return state;
  }
}

function analysisDialog(state = null, action) {
  switch (action.type) {
    case ANALYSIS_DIALOG:
      return action.payload;
    default:
      return state;
  }
}

function loginNeeded(state = false, action) {
  switch (action.type) {
    case LOGIN_NEEDED:
      return action.payload;
    default:
      return state;
  }
}

// HACK: this is needed by async item fetching
// TODO: find better way to meet these needs…
const FORCE_STATE_UPDATE = "FORCE_STATE_UPDATE";
function stateCounter(state = 0, action) {
  switch (action.type) {
    case FORCE_STATE_UPDATE:
      return state + 1;
    default:
      return state;
  }
}

const TRIGGER_SCROLL_HELPER = "TRIGGER_SCROLL_HELPER";
function scrollHelperState(state = null, action) {
  switch (action.type) {
    case TRIGGER_SCROLL_HELPER:
      return { marker: action.payload };
    default:
      // ~HACK: ± *any* change to app state risks upsetting our connecting line, so we aggressively "release" it…
      return null;
  }
}

const TOURS_TOKEN = "TOURS_TOKEN";
function toursToken(state = "", action) {
  switch (action.type) {
    case TOURS_TOKEN:
      return action.payload;
    default:
      return state;
  }
}

// TODO: migrate immutable data items into reselect instead of root store
function survey(state = data.survey, action) {
  return state;
}
function target(state = data.target, action) {
  return state;
}
function overall(state = data.essentials.overall, action) {
  return state;
}
function essentials(state = data.essentials, action) {
  return state;
}
function measures(state = data.measures, action) {
  return state;
}
function items(state = data.items, action) {
  return state;
}
function schools(state = data.schools, action) {
  return state;
}

let store = createStore(
  combineReducers({
    stateCounter,
    scrollHelperState,
    toursToken,
    survey,
    target,
    activeLevel,
    activeSlug,
    activeView,
    activeComparisonSlug,
    activeFilter,
    customFilterMeasures,
    pendingFilterMeasures,
    filterEditorQuery,
    selectedLevel,
    selectedSlug,
    groupView,
    expandedResults,
    expandedItems,
    sortColumn,
    notes,
    analyses,
    loginNeeded,
    analysisDialog,
    overall,
    essentials,
    measures,
    items,
    schools,
  }),
);

const includeState = (state) => state; // TODO: reduce reliance on this for improved cacheability
const activeObject = (state) => state[state.activeLevel][state.activeSlug];
const selectedObject = createSelector(
  includeState,
  activeObject,
  (state, activeObject) => {
    if (!state.selectedLevel || !state.selectedSlug) return null;
    else {
      let [mainSlug, variantSlug] = state.selectedSlug.split("/");
      let mainObject = state[state.selectedLevel][mainSlug];
      return variantSlug
        ? mainObject._comparisons.byVariant[variantSlug]
        : mainObject;
    }
  },
);
const customFilterObjects = createSelector(
  includeState,
  ({ customFilterMeasures, measures }) =>
    customFilterMeasures ? customFilterMeasures.map((s) => measures[s]) : [],
);
const pendingFilterObjects = createSelector(
  includeState,
  ({ pendingFilterMeasures, measures }) =>
    pendingFilterMeasures
      ? pendingFilterMeasures.map((s) => measures[s]).filter((s) => Boolean(s))
      : [],
);
const unusedFilterObjects = createSelector(
  includeState,
  ({ pendingFilterMeasures, measures }) => {
    let allMeasureSlugs = measures.all._children.map((d) => d.slug);
    let unusedSlugs = pendingFilterMeasures
      ? allMeasureSlugs.filter((s) => pendingFilterMeasures.indexOf(s) === -1)
      : allMeasureSlugs;
    return unusedSlugs.map((s) => measures[s]);
  },
);

const fullyActiveFilter = createSelector(
  includeState,
  activeObject,
  ({ activeFilter }, activeObject) =>
    activeObject._alias === "all_measures" && activeFilter !== null
      ? activeFilter
      : null,
);
const childObjects = createSelector(
  includeState,
  activeObject,
  fullyActiveFilter,
  customFilterObjects,
  (
    { activeView, sortColumn },
    activeObject,
    fullyActiveFilter,
    customFilterObjects,
  ) => {
    let sortLimit = null;
    let unsortedObjects = [];
    let allChildren = activeObject._children;
    if (!fullyActiveFilter) {
      unsortedObjects = allChildren;
    } else if (fullyActiveFilter.parent_slug) {
      unsortedObjects = allChildren.filter(
        (m) => m.parent_slug === fullyActiveFilter.parent_slug,
      );
    } else if (fullyActiveFilter.hide_parent) {
      unsortedObjects = allChildren.filter(
        (m) => m.parent_slug !== fullyActiveFilter.hide_parent,
      );
    } else if (fullyActiveFilter.subject) {
      unsortedObjects = allChildren.filter(
        (m) => m.subject === fullyActiveFilter.subject,
      );
    } else if (fullyActiveFilter.custom) {
      unsortedObjects = customFilterObjects;
    } else if (fullyActiveFilter.sorted) {
      unsortedObjects = topSorted(
        allChildren,
        FILTER_MAPPINGS[fullyActiveFilter.sorted],
      );
    }

    return activeObject._child_type === "items"
      ? topSorted(unsortedObjects, "-difficulty", null)
      : topSorted(unsortedObjects, sortColumn, null);
  },
);

const comparisonsAvailable = createSelector(activeObject, (activeObject) => {
  return activeObject._comparisons.families;
});

const comparisonObjects = createSelector(
  includeState,
  activeObject,
  ({ activeComparisonSlug }, activeObject) => {
    return activeObject._comparisons.byFamily[activeComparisonSlug];
  },
);

const graphObjects = createSelector(
  activeObject,
  childObjects,
  comparisonObjects,
  (activeObject, childObjects, comparisonObjects) => {
    if (activeObject._alias) return childObjects;
    else if (activeObject._child_type === "items")
      return comparisonObjects
        ? [activeObject, ...comparisonObjects]
        : [activeObject];
    else
      return comparisonObjects
        ? [activeObject, ...comparisonObjects]
        : [activeObject, ...childObjects];
  },
);

const _notesSelector = createSelector(includeState, ({ notes }) => notes);
const notesCount = createSelector(
  _notesSelector,
  (notes) => Object.keys(notes).length,
);
const findNote = createSelector(
  _notesSelector,
  (notes) => (measure, context) => {
    let key = `${measure}/${context}`;
    return notes[key];
  },
);

const _analysesSelector = createSelector(
  includeState,
  ({ analyses }) => analyses,
);
const findAnalysis = createSelector(
  _analysesSelector,
  (analyses) => (measure) => analyses[measure],
);

const sharingModal = createSelector(
  includeState,
  ({ measures, analyses, analysisDialog: slug, notes }) => {
    let NC = data.noteContexts;
    let CONTEXT_ORDER = [NC.self, NC.parent_essential, NC.all_measures];
    return slug
      ? {
          NC,
          measure: measures[slug],
          analysis: analyses[slug],
          notes: {
            list: CONTEXT_ORDER.map((ctx) => notes[`${slug}/${ctx}`]).filter(
              (d) => d && d.observation,
            ),
          },
        }
      : null;
  },
);

const derivedState = combineSelectors({
  activeObject,
  selectedObject,
  fullyActiveFilter,
  childObjects,
  customFilterObjects,
  pendingFilterObjects,
  unusedFilterObjects,
  comparisonsAvailable,
  comparisonObjects,
  graphObjects,
  notesCount,
  findNote,
  findAnalysis,
  sharingModal,
});

let make_connected = connect((state) => ({ ...state, ...derivedState(state) }));

let WrappedNav = make_connected(Nav);

class _TrackURL extends AbstractTrackURL {
  static basePathname = links.report_intro;

  updateAppState(urlParts, initialCtx) {
    let dispatch = initialCtx || this.props.dispatch;

    let { path, fragPath, fragParams } = urlParts;

    let [level, slug] = path;
    if (!slug) {
      slug = level === "essentials" ? "overall" : "all";
    }
    dispatch({ type: SET_LEVEL_AND_SLUG, payload: [level, slug] });

    let [view] = fragPath;
    if (view) dispatch({ type: SET_ACTIVE_VIEW, payload: view });

    let {
      s: selection,
      g: group,
      c: comparison,
      f: filters,
      o: order,
      i: items,
      note: showNote,
      tour,
    } = fragParams;
    if (group) {
      let expResults = false;
      if (group[0] === "+") {
        group = group.slice(1);
        expResults = true;
      }
      dispatch({ type: SET_GROUP_VIEW, payload: group });
      dispatch({ type: SET_EXPANDED_RESULTS, payload: expResults });
    }
    if (comparison) {
      dispatch({ type: SET_ACTIVE_COMPARISON, payload: comparison });
    } else {
      dispatch({ type: SET_ACTIVE_COMPARISON, payload: null });
    }
    if (filters) {
      let [filter, value] = filters.split(/\]|\[/g);
      if (filter === "custom") {
        let slugs = value.split(",");
        dispatch({ type: FINISH_CUSTOM_FILTER_EDITING, payload: slugs });
        value = true;
      }
      if (filter === "subject") {
        value = +value;
      }
      dispatch({ type: SET_ACTIVE_FILTER, payload: { [filter]: value } });
    } else {
      dispatch({ type: SET_ACTIVE_FILTER, payload: null });
    }
    if (order) {
      dispatch({ type: SORT_COLUMN, payload: order });
    }
    if (items) {
      let expItems = items.split(",");
      dispatch({ type: SET_EXPANDED_ITEMS, payload: expItems });
    } else {
      dispatch({ type: SET_EXPANDED_ITEMS, payload: [] });
    }
    // NOTE: we select *after* filtering, since the latter clears selection right now, https://trello.com/c/1YCn3vxn/387-ui-bug-handle-case-where-selectedobject-gets-filtered-away
    if (selection) {
      let [selLevel, selSlug, ...extra] = selection.split("/");
      if (extra.length) selSlug = [selSlug, ...extra].join("/");
      dispatch({ type: SELECT_LEVEL_AND_SLUG, payload: [selLevel, selSlug] });
    } else {
      // (this would already be the case if it weren't for the intervening SET_ACTIVE_FILTER call…see note above.)
      dispatch({ type: SELECT_LEVEL_AND_SLUG, payload: [level, slug] });
    }

    if (tour || !initialCtx) {
      if (window.impact_tours) window.impact_tours.setToken(tour || "");
    }

    if (showNote) {
      let [slug, ctx] = showNote.split("/");
      dispatch({ type: NOTE_EDIT, payload: [slug, ctx] });
      dispatch({ type: SELECT_LEVEL_AND_SLUG, payload: ["measures", slug] });
      dispatch({ type: TRIGGER_SCROLL_HELPER, payload: null });
    }
  }

  generateUrlParts() {
    let {
      activeObject,
      activeView,
      selectedObject,
      target,
      groupView,
      expandedResults,
      expandedItems,
      sortColumn,
      activeComparisonSlug,
      fullyActiveFilter,
      customFilterMeasures,
      toursToken,
    } = this.props;

    let path = [activeObject._level];
    if (!activeObject._alias || activeObject._alias === "supplementals") {
      path.push(activeObject.slug);
    }
    path.push("");

    let fragPath = [activeView];

    let fragParams = {};
    if (selectedObject && selectedObject !== activeObject) {
      fragParams["s"] = `${selectedObject._level}/${selectedObject.slug}`;
    }
    if (target.is_group && activeObject._alias !== "all_measures") {
      fragParams["g"] = expandedResults ? `+${groupView}` : groupView;
    }
    if (activeComparisonSlug) {
      fragParams["c"] = activeComparisonSlug;
    }
    if (fullyActiveFilter) {
      let [key] = Object.keys(fullyActiveFilter);
      let val =
        key === "custom"
          ? customFilterMeasures.join(",")
          : fullyActiveFilter[key];
      fragParams["f"] = `${key}[${val}]`;
    }
    if (sortColumn) {
      fragParams["o"] = sortColumn;
    }
    if (expandedItems.length) {
      fragParams["i"] = expandedItems.join(",");
    }

    if (toursToken) {
      fragParams["tour"] = toursToken;
    }

    if (window.impact_tours) {
      // ~HACK: use this opportunity to update tours layout too
      window.impact_tours.updateLayout();
    }

    return { path, fragPath, fragParams };
  }
}

let TrackURL = make_connected(_TrackURL);

let NoteIndicator = make_connected(({ object, note, findAnalysis }) => {
  let analysis = findAnalysis(object.slug);
  return (
    <span className="status-icons">
      {note ? <GlyphExistingNote /> : null}
      {analysis && !analysis._placeholder ? <GlyphSharedMeasure /> : null}
    </span>
  );
});

let NoteButton = make_connected(({ object, note, noteContext, dispatch }) => {
  if (object._level === "measures" && links.notes_api)
    return (
      <button
        className="btn edit-note"
        onClick={(evt) => {
          evt.stopPropagation();
          dispatch({ type: NOTE_EDIT, payload: [object.slug, noteContext] });
        }}
      >
        <span className={`glyphicon glyphicon-${note ? "edit" : "plus"}`} />{" "}
        Note
      </button>
    );
  else return null;
});

let NoteEditor = make_connected(({ note, findAnalysis, dispatch }) => {
  let analysis = findAnalysis(note.measure);

  let buttonLabel;
  if (note._editing === "save") buttonLabel = "Saving…";
  else if (note._editing === "error") buttonLabel = "Retry";
  else buttonLabel = "Save";

  let handleClose = (evt) => {
    evt.preventDefault();

    let { measure, context, _modified } = note;
    if (_modified) {
      let ok = confirm(
        "Close without saving changes? This note will be reverted to its previous state!",
      );
      if (!ok) return;
    }
    dispatch({ type: NOTE_EXIT, payload: [measure, context] });
  };

  let handleFormSubmit = (evt) => {
    evt.preventDefault();

    let { measure, context, observation } = note;
    dispatch({ type: NOTE_SAVE, payload: [measure, context, observation] });
    $.ajax(links.notes_api + `?m=${measure}&c=${context}`, {
      type: observation ? "put" : "delete",
      data: observation,
      success: (d) => {
        let [_, _modified] = d.split(",");
        let fields = observation
          ? {
              when_modified: new Date(_modified),
            }
          : null;
        dispatch({ type: NOTE_DONE, payload: [measure, context, fields] });
      },
      error: (jqXHR) => {
        dispatch({ type: NOTE_FAIL, payload: [measure, context] });
        if (jqXHR.status === 403) {
          dispatch({ type: LOGIN_NEEDED, payload: true });
        }
      },
    });
  };

  let handleObservationInput = (evt) => {
    let { measure, context } = note;
    let observation = evt.target.value;
    dispatch({ type: NOTE_TEXT, payload: [measure, context, observation] });
  };

  let handleShare = (evt) => {
    evt.preventDefault();

    let { measure } = note;
    dispatch({ type: ANALYSIS_DIALOG, payload: measure });
  };

  return (
    <div className="note-editor">
      <h3>What do you notice about this Measure?</h3>
      <CloseButton label="Close editor." onClick={handleClose} />
      <form onSubmit={handleFormSubmit}>
        <textarea
          className="user-notes"
          placeholder="I observe that… &#10;Some patterns/trends I notice… &#10;I'm surprised that I see… &#10;A question I have is… &#10;It strikes me as significant that…"
          value={note.observation}
          onChange={handleObservationInput}
        />
        {note._editing === "error" ? (
          <span className="save-error">An error occurred saving note</span>
        ) : null}
        <button
          type="submit"
          className="save-edits"
          disabled={!note._modified || note._editing === "save"}
        >
          {buttonLabel}
        </button>
        {analysis._placeholder ? (
          note._original ? (
            <button
              type="button"
              className="btn open-analysis"
              onClick={handleShare}
            >
              Share
            </button>
          ) : null
        ) : (
          <a className="open-analysis" onClick={handleShare}>
            Shared as a{" "}
            {analysis.needs_growth
              ? "Measure for Growth"
              : "Measure of Strength"}
          </a>
        )}
        {note.when_modified ? (
          <span className="when-modified">
            Last modified:{" "}
            <span className="timestamp">
              {verboseTimestamp(note.when_modified)}
            </span>
          </span>
        ) : null}
      </form>
    </div>
  );
});

let TableRow = ({
  object: d,
  note = null,
  attachedComparison = false,
  tableVars: {
    rows,
    selectedObject,
    activeView,
    survey,
    dispatch,
    _parentColumn,
    _respondentColumn,
  },
}) => (
  <tr
    onClick={() => {
      // comparisons suppress child objects within graph, so clear when selecting one here.
      if (rows === "childObjects")
        dispatch({ type: SET_ACTIVE_COMPARISON, payload: null });
      // [we need to do this *after* clearing comparison, else comparison change clears selection…]
      dispatch({ type: SELECT_LEVEL_AND_SLUG, payload: [d._level, d.slug] });
    }}
    className={d === selectedObject ? "selected" : ""}
    data-path={`${d._level}/${d.slug}`}
  >
    <td>
      <div className="name-flex-wrapper">
        {survey.product_key === S.ECE && d._level === "essentials" ? (
          <IndividualGraphicRep survey={survey} item={d} />
        ) : null}
        <span className="entity-name">{d.name}</span>
      </div>
    </td>
    {activeView == "change" ? (
      <td>
        <ChangeDisplay result={d._results.latest} />
      </td>
    ) : null}
    <td>
      <ComboScore item={d} result={d._results.latest} />
    </td>
    {_parentColumn && !attachedComparison ? (
      <td>
        <div className="parent-cell">
          <IndividualGraphicRep survey={survey} item={d._parent} />
          <span className="parent-name">{d._parent.name}</span>
        </div>
      </td>
    ) : null}
    {_parentColumn && attachedComparison ? <td /> : null}
    {_respondentColumn && !attachedComparison ? (
      <td>
        <span className="respondent-label">
          {simpleTitleCase(data.subjectLabels[d.subject])}
        </span>
      </td>
    ) : null}
    {_respondentColumn && attachedComparison ? <td /> : null}
    {attachedComparison ? (
      <td />
    ) : (
      <td>
        <NoteIndicator object={d} note={note} />
      </td>
    )}
  </tr>
);

let TableColumnHead = make_connected(
  ({ label, sort, tableVars: { rows }, sortColumn, dispatch }) => {
    if (rows === "childObjects") {
      let active = sortColumn.slice(1) === sort.slice(1);
      let toggleDir = active ? (sortColumn[0] === "-" ? "+" : "-") : sort[0];
      let sortIcon = active
        ? sortColumn[0] === "+"
          ? "sort-by-attributes"
          : "sort-by-attributes-alt"
        : null;

      return (
        <th
          className="sortable-column"
          onClick={(evt) =>
            dispatch({
              type: SORT_COLUMN,
              payload: `${toggleDir}${sort.slice(1)}`,
            })
          }
        >
          {label}{" "}
          {sortIcon ? (
            <span
              className={`active-sort-direction glyphicon glyphicon-${sortIcon}`}
            />
          ) : null}
        </th>
      );
    } else {
      return <th>{label}</th>;
    }
  },
);

let Table = make_connected(
  ({
    rows,
    inlineComparisons,
    activeObject,
    selectedObject,
    childObjects,
    comparisonObjects,
    activeView,
    findNote,
    survey,
    dispatch,
  }) => {
    let _noteContext = null,
      _parentColumn,
      _respondentColumn,
      _caption,
      _rows;
    let NC = data.noteContexts;
    if (rows === "activeObject") {
      if (activeObject._level === "measures") _noteContext = NC.self;
      _parentColumn = activeObject._level === "measures";
      _respondentColumn = activeObject._level === "measures";
      _caption = activeObject._level === "measures" ? "Measure" : "Essential";
      _rows = [activeObject];
    } else if (rows === "childObjects") {
      if (activeObject._alias === "all_measures")
        _noteContext = NC.all_measures;
      else if (activeObject._level === "essentials")
        _noteContext = NC.parent_essential;
      _parentColumn = activeObject._alias === "all_measures";
      _respondentColumn = activeObject._child_type === "measures";
      _caption =
        activeObject._child_type === "measures"
          ? "Measures"
          : L.overall_product_long;
      _rows = childObjects;
    } else if (rows === "comparisons") {
      _parentColumn = false;
      _caption = 0 ? "Comparison" : "Comparisons";
      _rows = comparisonObjects;
    } else {
      throw Error(`Unknown table rows option type: ${rows}`);
    }

    let _rowsAndNotes = [];
    if (_noteContext !== null)
      _rows.forEach((obj) => {
        let note = findNote(obj.slug, _noteContext);
        _rowsAndNotes.push([obj, note]);
        if (note && note._editing) {
          _rowsAndNotes.push([obj, note, "editor"]);
        } else if (obj === selectedObject) {
          _rowsAndNotes.push([obj, note, "actions"]);
        }
      });
    else _rowsAndNotes = _rows.map((obj) => [obj]);

    let _columnCount = countTruthyValues([
      "caption",
      activeView === "change",
      "performance",
      _parentColumn,
      _respondentColumn,
      "buttons",
    ]);
    let _localVars = {
      rows,
      selectedObject,
      activeView,
      survey,
      dispatch,
      _parentColumn,
      _respondentColumn,
    };
    return (
      <table className={"table showing-" + rows}>
        <thead>
          <tr>
            <TableColumnHead
              label={_caption}
              sort="+name"
              tableVars={_localVars}
            />
            {activeView === "change" ? (
              <TableColumnHead
                label="Change"
                sort="-change"
                tableVars={_localVars}
              />
            ) : null}
            <TableColumnHead
              label="Performance"
              sort="-score"
              tableVars={_localVars}
            />
            {_parentColumn ? (
              <TableColumnHead
                label="Essential"
                sort="+essential"
                tableVars={_localVars}
              />
            ) : null}
            {_respondentColumn ? (
              <TableColumnHead
                label="Respondent"
                sort="+respondent"
                tableVars={_localVars}
              />
            ) : null}
            <th />
          </tr>
        </thead>
        <tbody>
          {_rowsAndNotes.map(([object, note, isExtraRow]) =>
            !isExtraRow ? (
              <TableRow
                key={object.slug}
                object={object}
                note={note}
                tableVars={_localVars}
              />
            ) : isExtraRow === "editor" ? (
              <tr key={`${object.slug}-editor`} className="editor-row">
                <td colSpan={_columnCount}>
                  <NoteEditor note={note} />
                </td>
              </tr>
            ) : (
              <tr key={`${object.slug}-actions`} className="actions-row">
                <td colSpan={_columnCount}>
                  <NoteButton
                    object={object}
                    note={note}
                    noteContext={_noteContext}
                  />
                  {rows !== "activeObject" ? (
                    <button
                      className="btn view-details"
                      onClick={(evt) => {
                        evt.stopPropagation();
                        dispatch({
                          type: SET_LEVEL_AND_SLUG,
                          payload: [object._level, object.slug],
                        });
                      }}
                    >
                      Details
                    </button>
                  ) : null}
                </td>
              </tr>
            ),
          )}
          {rows === "activeObject" && inlineComparisons && comparisonObjects
            ? comparisonObjects.map((object) => (
                <TableRow
                  key={object.slug}
                  object={object}
                  note={null}
                  attachedComparison={true}
                  tableVars={_localVars}
                />
              ))
            : null}
        </tbody>
      </table>
    );
  },
);

let ChangeTable = (props) => (
  <section className="change-table">
    <ChangeTableHead {...props} />
    <ChangeTableBody {...props} />
  </section>
);

let ChangeTableHead = make_connected(
  ({
    object,
    activeObject,
    selectedObject,
    activeView,
    dispatch,
    findNote,
  }) => {
    let showButtons = activeView === "trends";
    let noteContext = null;
    if (showButtons && object._level === "measures") {
      let NC = data.noteContexts;
      if (activeObject._alias === "all_measures") noteContext = NC.all_measures;
      else if (activeObject._level === "essentials")
        noteContext = NC.parent_essential;
      else if (object === activeObject) noteContext = NC.self;
    }
    let note = null;
    if (noteContext !== null) {
      note = findNote(object.slug, noteContext);
    }
    return (
      <table className="table">
        <tbody>
          <tr
            className={object === selectedObject ? "selected" : null}
            onClick={(evt) =>
              dispatch({
                type: SELECT_LEVEL_AND_SLUG,
                payload: [object._level, object.slug],
              })
            }
          >
            <td>{object.name}</td>
            <td>
              {showButtons && noteContext !== null ? (
                <NoteButton
                  object={object}
                  note={note}
                  noteContext={noteContext}
                />
              ) : null}
              {showButtons &&
              object !== activeObject &&
              !object._is_comparison ? (
                <button
                  className="btn view-details"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    dispatch({
                      type: SET_LEVEL_AND_SLUG,
                      payload: [object._level, object.slug],
                    });
                  }}
                >
                  Details
                </button>
              ) : null}
            </td>
          </tr>
          {note && note._editing ? (
            <tr>
              <td>
                <NoteEditor note={note} />
              </td>
              <td></td>
            </tr>
          ) : null}
        </tbody>
      </table>
    );
  },
);

let ChangeTableBody = make_connected(({ object, activeView }) => {
  let results =
    activeView === "change" ? object._results.slice(-2) : object._results;
  return (
    <table className="table sub-table">
      <thead>
        <tr>
          {results.map((d, i) => (
            <th key={i}>{d.survey.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {results.map((d, i) => (
            <td key={i}>
              <RawScore item={object} result={d} />
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
});

let SchoolTableOverallCell = ({ item, result }) => (
  <ComboScore item={item} result={result} longSummary={true} />
);
let SchoolTableItemCell = ({ item, survey }) => (
  <span>
    <IndividualGraphicRep
      survey={survey}
      item={item}
      size={26}
      score_label={true}
    />
  </span>
);

let SchoolTable = make_connected(
  ({
    survey,
    target,
    schools,
    activeView,
    expandedResults,
    activeObject,
    childObjects,
    dispatch,
  }) => {
    let nSmao = /*schoolsMissingActiveObject*/ schools.filter((school) => {
      return !school[activeObject._level][activeObject.slug];
    }).length;
    return (
      <div className="school-table-wrapper">
        <table
          className={`table school-table ${
            activeObject._child_type !== "items" ? "expandible-collapsible" : ""
          }`}
        >
          <colgroup className="primary-columns" span={2} />
          <thead>
            <tr className="extra-ui-layout">
              <th className="padding" colSpan={2} />
              {activeObject._child_type !== "items" ? (
                expandedResults ? (
                  <th
                    colSpan={childObjects.length}
                    className="table-legend school-table-legend"
                  >
                    <GroupExpandResultsToggle />
                    <PuzzleLegend allRanks={data.allRanks} />
                  </th>
                ) : (
                  <th className="table-legend school-table-legend">
                    <GroupExpandResultsToggle />
                    <OverlayTrigger
                      trigger="click"
                      rootClose
                      placement="left"
                      overlay={
                        <Popover id="result-basis-popup">
                          <ResultBasis />
                        </Popover>
                      }
                    >
                      <span className="glyphicon glyphicon-info-sign" />
                    </OverlayTrigger>
                  </th>
                )
              ) : null}
            </tr>
            <tr>
              <th>School</th>
              <th>
                {activeObject._alias === "5e_overall"
                  ? survey.labels.overall
                  : activeObject.name}
              </th>
              {expandedResults
                ? childObjects.map((d) => <th key={d.slug}>{d.name}</th>)
                : null}
            </tr>
          </thead>
          <tbody>
            <tr className="group-aggregate">
              <td>{target.name}</td>
              <td>
                {activeObject._level === "measures" ? (
                  <SchoolTableItemCell item={activeObject} survey={survey} />
                ) : !activeObject._alias ? (
                  <SchoolTableOverallCell
                    item={activeObject}
                    result={activeObject._results.latest}
                  />
                ) : null}
                {activeView === "change" && !activeObject._alias ? (
                  <ChangeDisplay result={activeObject._results.latest} />
                ) : null}
              </td>
              {expandedResults ? (
                childObjects.map((d) => (
                  <td key={d.slug}>
                    <a
                      href="#"
                      onClick={(evt) => {
                        evt.preventDefault();
                        dispatch({
                          type: SET_LEVEL_AND_SLUG,
                          payload: [d._level, d.slug],
                        });
                      }}
                    >
                      <SchoolTableItemCell survey={survey} item={d} />
                      {activeView === "change" ? (
                        <ChangeDisplay result={d._results.latest} />
                      ) : null}
                      <span className="glyphicon glyphicon-triangle-right" />
                    </a>
                  </td>
                ))
              ) : (
                <th
                  className="school-table-overview"
                  rowSpan={1 + schools.length}
                >
                  {!activeObject._alias ? (
                    <div
                      className="description"
                      dangerouslySetInnerHTML={activeObject.text.overview}
                    />
                  ) : null}
                </th>
              )}
            </tr>
            {schools.map((school) => {
              let schoolObject = school[activeObject._level][activeObject.slug];
              return schoolObject ? (
                <tr
                  key={school.slug}
                  onClick={(evt) => {
                    evt.preventDefault();
                    window.location =
                      school.url; /* TODO: preserve relevant state */
                  }}
                >
                  <td>{school.name}</td>
                  <td>
                    {activeObject._level === "measures" ? (
                      <SchoolTableItemCell
                        survey={survey}
                        item={schoolObject}
                      />
                    ) : (
                      <SchoolTableOverallCell
                        item={schoolObject}
                        result={schoolObject._results.latest}
                      />
                    )}
                    {activeView === "change" && !activeObject._alias ? (
                      <ChangeDisplay result={schoolObject._results.latest} />
                    ) : null}
                  </td>
                  {expandedResults
                    ? childObjects
                        .map(
                          (groupObj) =>
                            school[groupObj._level][groupObj.slug] || {
                              slug: groupObj.slug,
                              _level: groupObj._level,
                              _results: { latest: { score: null, code: "X" } },
                            },
                        )
                        .map((d) => (
                          <td key={d.slug}>
                            <SchoolTableItemCell survey={survey} item={d} />
                            {activeView === "change" ? (
                              <ChangeDisplay result={d._results.latest} />
                            ) : null}
                          </td>
                        ))
                    : null}
                </tr>
              ) : null;
            })}
          </tbody>
        </table>
        {nSmao ? (
          <p className="omitted-missing-data-schools">
            {activeObject.name} is a {data.levelLabels[activeObject.level]}–only
            measure. {nSmao} school{plural(nSmao)} in this group{" "}
            {plural(nSmao, "have", "has")} been omitted from this list.
          </p>
        ) : null}
      </div>
    );
  },
);

function workaroundRBS541() {
  // HACK: react-bootstrap dropdowns don't behave as expected after you click an item.
  //       This is one way to workaround, HT https://github.com/react-bootstrap/react-bootstrap/issues/541#issuecomment-265821331
  let clickEvt;
  try {
    clickEvt = new MouseEvent("click");
  } catch (e) {
    // fallback to old event API for IE11
    clickEvt = document.createEvent("MouseEvents");
    clickEvt.initEvent("click", true, true);
  }
  document.dispatchEvent(clickEvt);
}

let PagePickers = make_connected(({ activeObject, dispatch }) => (
  <section className="page-pickers">
    <Button
      active={activeObject._alias === "all_measures"}
      onClick={(evt) =>
        dispatch({ type: SET_LEVEL_AND_SLUG, payload: ["measures", "all"] })
      }
    >
      <h1>All Measures</h1>
    </Button>
    <EssentialsMenu />
  </section>
));

let EssentialsMenu = make_connected(
  ({ essentials, measures, activeObject, dispatch }) => {
    let expandedEssential = null,
      menuCrumb = null;
    if (activeObject._child_type === "measures") {
      expandedEssential = activeObject;
      menuCrumb = essentials.overall.name;
    } else if (activeObject._child_type === "items") {
      expandedEssential = essentials[activeObject.parent_slug];
      menuCrumb = `${essentials.overall.name} / ${expandedEssential.name}`;
    }

    let menuTitle =
      activeObject._alias === "all_measures" ? (
        <div className="menu-title">
          <span className="main">{essentials.overall.name}</span>
        </div>
      ) : (
        <div className="menu-title">
          {menuCrumb ? <span className="crumb">{menuCrumb}</span> : null}
          <span className="main">{activeObject.name}</span>
        </div>
      );

    let ObjectMenuItem = ({ object, indent }) => (
      <MenuItem
        key={object.slug}
        active={object === activeObject}
        className={`indent-${indent}`}
        onSelect={(evt) => {
          workaroundRBS541();
          dispatch({
            type: SET_LEVEL_AND_SLUG,
            payload: [object._level, object.slug],
          });
        }}
      >
        {object.name}
      </MenuItem>
    );

    let menuOptions = [];
    let _essentials = [
      ...essentials.overall._children,
      essentials._supplemental,
    ];
    menuOptions.push(
      <ObjectMenuItem key="overall" object={essentials.overall} indent={0} />,
    );
    _essentials.forEach((obj) => {
      if (obj._alias === "supplementals")
        menuOptions.push(<MenuItem key={"supp-div"} divider />);
      menuOptions.push(
        <ObjectMenuItem key={`e-${obj.slug}`} object={obj} indent={1} />,
      );
      if (obj === expandedEssential)
        obj._children.forEach((obj) => {
          menuOptions.push(
            <ObjectMenuItem key={`m-${obj.slug}`} object={obj} indent={2} />,
          );
        });
    });

    return (
      <DropdownButton
        id="jump-menu"
        bsStyle="default"
        title={menuTitle}
        active={activeObject._alias !== "all_measures"}
      >
        {menuOptions}
      </DropdownButton>
    );
  },
);

let GroupViewToggle = make_connected(({ groupView, dispatch }) => {
  return (
    <ButtonGroup className="school-toggle">
      <Button
        active={groupView === "aggregate"}
        onClick={(evt) =>
          dispatch({ type: SET_GROUP_VIEW, payload: "aggregate" })
        }
      >
        Aggregate
      </Button>
      <Button
        active={groupView === "by_school"}
        onClick={(evt) =>
          dispatch({ type: SET_GROUP_VIEW, payload: "by_school" })
        }
      >
        By School
      </Button>
    </ButtonGroup>
  );
});

let GroupExpandResultsToggle = make_connected(
  ({ activeObject, expandedResults, dispatch }) => {
    let verb = expandedResults ? "Hide" : "Show";
    let noun = activeObject._alias === "5e_overall" ? "Essentials" : "Measures";
    return (
      <button
        className="school-breakdown btn"
        onClick={(evt) =>
          dispatch({ type: SET_EXPANDED_RESULTS, payload: !expandedResults })
        }
      >
        {verb} {noun}
      </button>
    );
  },
);

let YearViews = make_connected(
  ({ survey, activeView, groupView, dispatch }) => (
    <section>
      <h4>Data Range</h4>
      <div id="data-ranges">
        {[
          {
            viewKey: "performance",
            minYears: 1,
            question: "1 Year Performance",
            icon: <GlyphRangeOneYear />,
          },
          {
            viewKey: "change",
            minYears: 2,
            question: "2 Year Change",
            icon: <GlyphRangeTwoYears />,
          },
          {
            viewKey: "trends",
            minYears: 3,
            question: "All Year Trends",
            icon: <GlyphRangeAllYears />,
          },
        ]
          .filter(
            (d) =>
              survey._fullYears.length >= d.minYears &&
              (groupView !== "by_school" || d.viewKey !== "trends"),
          )
          .map((d) => (
            <button
              key={d.viewKey}
              type="button"
              className={`btn btn-default ${
                activeView == d.viewKey ? "active" : ""
              }`}
              onClick={(evt) =>
                dispatch({ type: SET_ACTIVE_VIEW, payload: d.viewKey })
              }
            >
              {d.icon} <span className="button-text">{d.question}</span>
            </button>
          ))}
      </div>
    </section>
  ),
);

let DrivingQuestion = make_connected(({ activeView, activeObject, target }) => {
  let question;
  switch (activeView) {
    case "performance":
      question = `How is ${target.short_name} performing on ${
        activeObject._alias === "5e_overall" ? "each of the " : ""
      }${
        activeObject._alias && activeObject._child_type === "measures"
          ? activeObject.name.toLowerCase()
          : activeObject.name
      }?`;
      break;
    case "change":
      question = "How has performance changed since the last survey?";
      break;
    case "trends":
      question = "How has performance changed across all surveys?";
      break;
  }
  return <h1 className="driving-question">{question}</h1>;
});

let ResultBasis = make_connected(
  ({ survey, target, activeObject, groupView }) => {
    let narrative,
      heading = "What are these results based on?";
    if (groupView === "by_school") {
      if (activeObject._alias === "5e_overall") {
        narrative = `This group’s ${survey.labels.overall} performance is based on the ${survey.labels.overall_product}. Expand this table to learn more about these underlying Essentials.`;
      } else {
        narrative =
          "This group’s performance on this Essential is based on its Measures. Expand this table to learn more about this Essential’s underlying concepts (measures) and their related survey questions.";
      }
    } else if (activeObject._alias === "5e_overall") {
      narrative = `This ${target.type_label}’s overall performance is based on the ${L.overall_product} shown below. Click on each row to learn more about each Essential and its underlying concepts (measures).`;
    } else if (activeObject._level === "essentials") {
      narrative = `This ${target.type_label}’s performance on this Essential is based on the Measures shown below. Click on each row to learn more about this Essential’s underlying concepts (measures) and their related survey questions.`;
    } else if (
      activeObject._level === "measures" &&
      activeObject.slug === "all"
    ) {
      heading = "What results are shown below?";
      narrative = `This page displays core teacher and student measures from all ${L.overall_product} as well as any extra supplemental teacher and student measures.`;
    } else if (activeObject._level === "measures") {
      narrative = `${activeObject.name} is comprised of the survey questions shown below. ${target.name}’s performance on individual survey questions is available below to identify particular strengths and weaknesses on ${activeObject.name}`;
    } else {
      narrative = ""; // unexpected case…
    }
    return (
      <div className="results-basis">
        <h3>{heading}</h3>
        <p>{narrative}</p>
      </div>
    );
  },
);

let Comparisons = make_connected(
  ({ activeObject, activeComparisonSlug, comparisonsAvailable, dispatch }) => {
    return (
      <section>
        <h4>
          Comparisons{" "}
          {activeComparisonSlug ? (
            <a
              href="#"
              onClick={(evt) => {
                evt.preventDefault();
                dispatch({ type: SET_ACTIVE_COMPARISON, payload: null });
              }}
            >
              Clear all
              <span className="glyphicon glyphicon-remove" />
            </a>
          ) : null}
        </h4>
        <div id="comparison-items">
          {comparisonsAvailable.map((cmp) => (
            <button
              key={cmp.slug}
              className={`btn btn-default ${
                cmp.slug === activeComparisonSlug ? "active" : ""
              }`}
              type="button"
              onClick={(evt) =>
                dispatch({ type: SET_ACTIVE_COMPARISON, payload: cmp.slug })
              }
            >
              {cmp.name}
            </button>
          ))}
        </div>
      </section>
    );
  },
);

let FiltersWrapper = make_connected(({ pendingFilterMeasures }) =>
  pendingFilterMeasures ? <FilterEditor /> : <Filters />,
);

let FilterEditor = make_connected(
  ({
    filterEditorQuery,
    pendingFilterObjects,
    unusedFilterObjects,
    dispatch,
  }) => {
    return (
      <div className="filter-editor">
        <h5>Select Measures</h5>
        <form
          className="selection-group"
          onSubmit={(evt) => {
            evt.preventDefault();
            dispatch({
              type: ADD_PENDING_FILTER,
              payload: evt.currentTarget.elements[0].value,
            });
            evt.currentTarget.reset();
          }}
        >
          <Autocomplete
            className="search-box"
            value={filterEditorQuery}
            onChange={(evt) =>
              dispatch({
                type: SET_FILTER_QUERY,
                payload: evt.currentTarget.value,
              })
            }
            items={unusedFilterObjects}
            getItemValue={(d) => d.slug}
            onSelect={(s) => dispatch({ type: ADD_PENDING_FILTER, payload: s })}
            inputProps={{
              className: "search-box",
              placeholder: "Type to search measures",
            }}
            shouldItemRender={(d, val) =>
              d.name.toLowerCase().indexOf(val.toLowerCase()) !== -1
            }
            renderItem={(d, isHighlighted) => (
              <div
                className={`suggestion ${isHighlighted ? "highlighted" : null}`}
                key={d.slug}
              >
                {d.name}
              </div>
            )}
          />
          <ul className="selected-measures">
            {pendingFilterObjects.map((m) => (
              <li key={m.slug}>
                {m.name}{" "}
                <span
                  className="glyphicon glyphicon-remove remove-measure"
                  onClick={(evt) =>
                    dispatch({ type: REMOVE_PENDING_FILTER, payload: m.slug })
                  }
                />
              </li>
            ))}
          </ul>
        </form>
        <button
          type="button"
          className="btn save"
          onClick={(evt) =>
            dispatch({
              type: FINISH_CUSTOM_FILTER_EDITING,
              payload: pendingFilterObjects.map((m) => m.slug),
            })
          }
        >
          Save
        </button>
        <button
          type="button"
          className="btn cancel"
          onClick={(evt) => dispatch({ type: CANCEL_CUSTOM_FILTER_EDITING })}
        >
          Cancel
        </button>
      </div>
    );
  },
);

let Filters = make_connected(
  ({
    activeObject,
    activeView,
    activeFilter,
    customFilterMeasures,
    essentials,
    survey,
    dispatch,
  }) => {
    // NOTE: this is not a deep-equality test (or even a full shallow comparison), but should suffice for our purposes
    let isActive = activeFilter
      ? (f) => Object.keys(f).every((k) => f[k] === activeFilter[k])
      : (f) => false;

    let FilterLink = ({ icon, filter, children }) => (
      <button
        type="button"
        className={`btn btn-default ${isActive(filter) ? "active" : null}`}
        onClick={(evt) =>
          dispatch({ type: SET_ACTIVE_FILTER, payload: filter })
        }
      >
        {icon} <span className="button-text">{children}</span>
      </button>
    );

    let menuItems = [
      {
        label: `${L.overall_product} Measures`,
        filter: { hide_parent: "_supplemental" },
      },
      {
        label: "Student Measures",
        filter: { subject: data.subjectLabels["student"] },
      },
      ...(survey.product_key === S.ECE
        ? [
            {
              label: "Parent Measures",
              filter: { subject: data.subjectLabels["parent"] },
            },
          ]
        : []),
      {
        label: "Teacher Measures",
        filter: { subject: data.subjectLabels["teacher"] },
      },
      {
        label: "Supplemental Measures",
        filter: { parent_slug: "_supplemental" },
      },
      { divider: true },
      ...essentials.overall._children.map((obj) => ({
        label: `Measures from ${obj.name}`,
        filter: { parent_slug: obj.slug },
      })),
      { divider: true },
      ...(customFilterMeasures
        ? [
            {
              label: `My Custom Group (${customFilterMeasures.length})`,
              filter: { custom: true },
            },
            {
              label: "Edit Custom Group",
              filter: {
                _editCustom: true,
                pendingFilterMeasures: customFilterMeasures,
              },
            },
          ]
        : [
            {
              label: "Add Custom Group",
              filter: { _editCustom: true, pendingFilterMeasures: [] },
            },
          ]),
    ];
    let activeItem = menuItems.find((m) => m.filter && isActive(m.filter));

    return (
      <section>
        <h4>
          Filters{" "}
          {activeFilter ? (
            <a
              href="#"
              onClick={(evt) => {
                evt.preventDefault();
                dispatch({ type: SET_ACTIVE_FILTER, payload: null });
              }}
            >
              Clear<span className="glyphicon glyphicon-remove"></span>
            </a>
          ) : null}
        </h4>
        <div id="filter-questions">
          <div>
            <FilterLink
              filter={{ sorted: FILTER_HIGHEST }}
              icon={<GlyphFilterPerformanceHighest />}
            >
              Highest Performing Measures
            </FilterLink>
            <FilterLink
              filter={{ sorted: FILTER_LOWEST }}
              icon={<GlyphFilterPerformanceLowest />}
            >
              Lowest Performing Measures
            </FilterLink>
          </div>
          {activeView !== "performance" ? (
            <div>
              <FilterLink
                filter={{ sorted: FILTER_INCREASE }}
                icon={<GlyphFilterTrendIncrease />}
              >
                Most Improved Measures
              </FilterLink>
              <FilterLink
                filter={{ sorted: FILTER_DECREASE }}
                icon={<GlyphFilterTrendDecrease />}
              >
                Most Declined Measures
              </FilterLink>
            </div>
          ) : null}
        </div>
        <DropdownButton
          id="more-filters-menu"
          active={Boolean(activeItem)}
          title={activeItem ? activeItem.label : "Filter by Measure Group"}
          onToggle={(open) => {
            if (open)
              setTimeout(
                () =>
                  scrollIntoViewIfNeeded(
                    ".dropdown-menu[aria-labelledby=more-filters-menu]",
                    false,
                  ),
                0,
              );
          }}
        >
          {menuItems.map((m, idx) =>
            m.divider ? (
              <MenuItem key={idx} divider />
            ) : (
              <MenuItem
                key={idx}
                active={m === activeItem}
                onClick={(evt) =>
                  dispatch({ type: SET_ACTIVE_FILTER, payload: m.filter })
                }
              >
                {m.label}
              </MenuItem>
            ),
          )}
        </DropdownButton>
      </section>
    );
  },
);

let WrappedPopupGraph = make_connected(PopupGraph);

data._measureStatus = {}; // ~HACK: we want to track this even when accordion gets unmounted
let WrappedAsyncAccordions = make_connected(AsyncAccordions);

// HACK: ScrollHelper needs to track the survey items and measure resource accordion size changes…
$(document.body).on(
  "shown.inq.collapse hidden.inq.collapse",
  ".collapse",
  (evt) => {
    store.dispatch({ type: FORCE_STATE_UPDATE });
  },
);

let Report = make_connected(
  ({
    survey,
    target,
    activeObject,
    childObjects,
    graphObjects,
    activeView,
    groupView,
    expandedResults,
    selectedObject,
    scrollHelperState,
    activeComparisonSlug,
    loginNeeded,
    sharingModal,
    dispatch,
  }) => (
    <section id="report-body" data-active-view={activeView}>
      <div className="grid report-grid">
        <div id="report-sidebar" className="column col-narrow">
          <PagePickers />
          {target.is_group && activeObject._alias !== "all_measures" ? (
            <GroupViewToggle />
          ) : null}
          <YearViews />
          {activeView !== "performance" && !activeObject._alias ? (
            <ChangeTable object={activeObject} />
          ) : null}
          {activeView !== "performance" &&
          selectedObject &&
          selectedObject !== activeObject ? (
            <ChangeTable object={selectedObject} />
          ) : null}
          {activeObject._alias === "all_measures" ? (
            <FiltersWrapper />
          ) : !activeObject._alias &&
            groupView !== "by_school" &&
            survey.product_key !== S.ECE ? (
            <Comparisons />
          ) : null}
        </div>
        <div className="report-bg">
          <DrivingQuestion />
          <div
            id="report-graph"
            className={`column graph-${activeView} ${
              activeView === "trends" || groupView === "by_school"
                ? "col-superwide"
                : "col-narrow"
            }`}
          >
            {groupView === "by_school" ? (
              <SchoolTable />
            ) : (
              <WrappedPopupGraph
                objects={graphObjects}
                rankRanges={data.rankRanges}
                selectObject={(l, s) =>
                  dispatch({ type: SELECT_LEVEL_AND_SLUG, payload: [l, s] })
                }
                triggerScrollHelper={(el) =>
                  dispatch({ type: TRIGGER_SCROLL_HELPER, payload: el })
                }
              />
            )}
            <div className="connecting-line" />
          </div>
          {activeView !== "trends" && groupView !== "by_school" ? (
            <div id="report-details" className="column col-wide">
              <ScrollHelper
                activeObject={activeObject}
                selectedObject={selectedObject}
                scrollHelperState={scrollHelperState}
              >
                {!activeObject._alias ? (
                  <Table rows="activeObject" inlineComparisons={true} />
                ) : null}
                {!activeObject._alias ? (
                  <div
                    className="description"
                    dangerouslySetInnerHTML={activeObject.text.overview}
                  />
                ) : null}
                {target.is_group && activeObject._alias !== "all_measures" ? (
                  <Button
                    className="school-breakdown"
                    onClick={(evt) =>
                      dispatch({ type: SET_GROUP_VIEW, payload: "by_school" })
                    }
                  >
                    Explore School Breakdown
                  </Button>
                ) : null}
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="left"
                  overlay={
                    <Popover id="result-basis-popup">
                      <ResultBasis />
                    </Popover>
                  }
                >
                  <span className="glyphicon glyphicon-info-sign" />
                </OverlayTrigger>
                {activeObject._child_type !== "items" ? (
                  <Table rows="childObjects" />
                ) : (
                  <WrappedAsyncAccordions
                    links={links}
                    _measureStatus={data._measureStatus}
                    _processAsyncItems={data._processAsyncItems}
                    updateItems={(d) =>
                      dispatch({ type: SET_EXPANDED_ITEMS, payload: d })
                    }
                    forceUpdate={() => dispatch({ type: FORCE_STATE_UPDATE })}
                  />
                )}
                {activeObject._child_type === "measures" &&
                activeObject._alias !== "all_measures" ? (
                  <h3>
                    <a
                      className="explore-all-measures"
                      href="#"
                      onClick={(evt) => {
                        evt.preventDefault();
                        dispatch({
                          type: SET_LEVEL_AND_SLUG,
                          payload: ["measures", "all"],
                        });
                      }}
                    >
                      Explore All Measures
                    </a>
                  </h3>
                ) : null}
              </ScrollHelper>
            </div>
          ) : null}
          <div className="clearfix"></div>
        </div>
      </div>
      {loginNeeded ? (
        <LoginModal
          user={user}
          links={links}
          onSuccess={() => dispatch({ type: LOGIN_NEEDED, payload: false })}
        />
      ) : sharingModal ? (
        <NoteShareModal
          modalInfo={sharingModal}
          user={user}
          links={links}
          onClose={() => dispatch({ type: ANALYSIS_DIALOG, payload: null })}
          onDataChange={(d) =>
            dispatch({
              type: ANALYSIS_UPDATE,
              payload: [sharingModal.measure.slug, d],
            })
          }
          onSuccessfulSave={(_) =>
            dispatch({
              type: ANALYSIS_SAVED,
              payload: [sharingModal.measure.slug],
            })
          }
          onLoginNeeded={() => dispatch({ type: LOGIN_NEEDED, payload: true })}
        />
      ) : null}
    </section>
  ),
);

import { setupAjaxDefaults } from "./shared.js";
setupAjaxDefaults({ user });

_TrackURL.intializeState(store.dispatch);
if (window.impact_tours)
  impact_tours.watchToken((token) => {
    store.dispatch({ type: TOURS_TOKEN, payload: token });
  });
render(
  <Provider store={store}>
    <div>
      <TrackURL />
      <WrappedNav />
      <Report />
    </div>
  </Provider>,
  document.querySelector("#react_target"),
);
